import React, { useMemo, useState } from 'react';
import { isMobile } from '../../global/ScreenSizeHelper';
import FieldSales from './componentsJobs/FieldSalesRep';
import SocialMedia from './componentsJobs/SocialMedia';
import ReactNativeDeveloper from './componentsJobs/ReactNative';
import FullStackEngineer from './componentsJobs/FullStackDeveloper';
import DeliveryDriver from './componentsJobs/DeliveryDriver';

const HiringNowList = () => {
  const lastUpdated = 'January 9, 2026';

  const sections = useMemo(
    () => [
      { id: '1', title: '1. Field Sales Representative' },
      { id: '2', title: '2. Social Media & Content Manager' },
      { id: '3', title: '3. Software Engineer – Full Stack' },
      { id: '4', title: '4. React Native Mobile Developer' },
      { id: '5', title: '5. Delivery Driver' },
    ],
    []
  );

  const [selectedId, setSelectedId] = useState('1'); // default = Field Sales

  const styles = {
    page: {
      background: '#ffffff',
      color: '#111827',
      minHeight: '100vh',
      fontFamily:
        'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
    },
    headerWrap: {
      borderBottom: '1px solid #e5e7eb',
      position: 'sticky',
      top: 0,
      zIndex: 50,
      background: 'rgba(255,255,255,0.95)',
      backdropFilter: 'blur(8px)',
    },
    header: {
      maxWidth: 1200,
      margin: '0 auto',
      padding: '14px 16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
    },
    brand: { display: 'flex', flexDirection: 'column', lineHeight: 1.1 },
    brandTop: { fontWeight: 800, fontSize: 16 },
    brandSub: { fontSize: 12, color: '#6b7280' },
    btnRow: { display: 'flex', gap: 8, alignItems: 'center' },
    button: {
      border: '1px solid #e5e7eb',
      background: '#fff',
      padding: '8px 10px',
      borderRadius: 10,
      fontSize: 13,
      cursor: 'pointer',
      color: '#111827',
    },

    // ✅ Layout switches based on isMobile
    shell: {
      maxWidth: 1200,
      margin: '0 auto',
      padding: isMobile ? '14px 12px 70px' : '22px 16px 80px',
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '320px 1fr',
      gap: isMobile ? 14 : 24,
    },

    // ✅ TOC becomes normal block on mobile (no sticky)
    toc: {
      position: isMobile ? 'relative' : 'sticky',
      top: isMobile ? 'auto' : 92,
      alignSelf: 'start',
      border: '1px solid #e5e7eb',
      borderRadius: 14,
      padding: 14,
      background: '#fff',
      width: '100%',
    },
    tocTitle: { fontWeight: 800, fontSize: 13, color: '#111827', marginBottom: 8 },
    tocLink: (isActive) => ({
      display: 'block',
      padding: '8px 10px',
      borderRadius: 10,
      fontSize: 13,
      color: isActive ? '#111827' : '#374151',
      background: isActive ? '#f3f4f6' : 'transparent',
      cursor: 'pointer',
      textDecoration: 'none',
      lineHeight: 1.3,
      border: '1px solid transparent',
    }),

    contentCard: {
      border: '1px solid #e5e7eb',
      borderRadius: 14,
      background: '#fff',
      padding: isMobile ? '18px 16px' : '24px 22px',
      width: '100%',
    },

    h1: { fontSize: 26, margin: 0, fontWeight: 900, letterSpacing: '-0.02em' },
    meta: { marginTop: 8, color: '#6b7280', fontSize: 13 },
    hr: { border: 'none', borderTop: '1px solid #e5e7eb', margin: '18px 0' },
    h2: { fontSize: 18, margin: '26px 0 10px', fontWeight: 900, letterSpacing: '-0.01em' },
    p: { fontSize: 14.5, lineHeight: 1.75, margin: '10px 0', color: '#111827' },
    ul: { margin: '10px 0 10px 22px', fontSize: 14.5, lineHeight: 1.75 },
    strongNotice: {
      border: '1px solid #fde68a',
      background: '#fffbeb',
      padding: 14,
      borderRadius: 12,
      fontSize: 14,
      lineHeight: 1.7,
      marginBottom: 14,
    },
    small: { fontSize: 12.5, color: '#6b7280', lineHeight: 1.6 },
    footer: {
      borderTop: '1px solid #e5e7eb',
      padding: '16px 16px',
      color: '#6b7280',
      fontSize: 12.5,
      maxWidth: 1200,
      margin: '0 auto',
    },
  };

  const renderRightPanel = () => {
    switch (selectedId) {
      case '1':
        return <FieldSales />;
      case '2':
        return <SocialMedia />;
      case '3':
        return <FullStackEngineer />;
      case '4':
        return <ReactNativeDeveloper />;
      case '5':
        return <DeliveryDriver />;
      default:
        return <FieldSales />;
    }
  };

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.headerWrap}>
        <div style={styles.header}>
          <div style={styles.brand}>
            <div style={styles.brandTop}>Hiring • Open Roles</div>
          </div>

          <div style={styles.btnRow}>
            <button
              style={styles.button}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              aria-label='Back to top'
            >
              Back to top
            </button>

            <button style={styles.button} onClick={() => window.print()} aria-label='Print'>
              Print
            </button>
          </div>
        </div>
      </div>

      {/* ✅ Layout: desktop = side-by-side, mobile = TOC on top then content */}
      <div style={styles.shell}>
        <aside style={styles.toc}>
          <div style={styles.tocTitle}>Jobs listed on this page</div>

          {sections.map((s) => (
            <div
              key={s.id}
              style={styles.tocLink(selectedId === s.id)}
              onClick={() => setSelectedId(s.id)}
              role='button'
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setSelectedId(s.id);
              }}
            >
              {s.title}
            </div>
          ))}

          <div style={{ marginTop: 10 }}>
            <div style={styles.small}>
              Tip: click a role to view details {isMobile ? 'below' : 'on the right'}.
            </div>
          </div>
        </aside>

        <main style={styles.contentCard}>
          <h1 style={styles.h1}>We’re Hiring</h1>
          <div style={{ ...styles.meta }}>
            {' '}
            Your ideas can shape the future and make a real impact
          </div>
          <div style={styles.meta}>
            Last Updated: <strong>{lastUpdated}</strong>
          </div>
          <hr style={styles.hr} />

          <div style={styles.strongNotice}>
            Apply if you’re comfortable working with a startup and moving fast.
          </div>

          {renderRightPanel()}

          <hr style={styles.hr} />
          <div style={styles.small}>
            © {new Date().getFullYear()} SingleCombo LLC. All rights reserved.
          </div>
        </main>
      </div>

      <style>{`
        @media print {
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          button { display: none !important; }
          aside { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default HiringNowList;
